(() => {
  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();
  const intersects = (left = [], right = []) =>
    left.some((value) => right.map(normalize).includes(normalize(value)));
  const includesId = (ids = [], id) => ids.some((value) => String(value) === String(id));

  function findOffer(data, campaigns, product, variant, codes, subscription, rate = 1) {
    if (!variant?.available || product.gift_card || !codes.length) return null;
    const tags = product.tags || [];
    const variantTags = variant.variant_tags || [];
    const sale = Number(variant.compare_at_price) > Number(variant.price);
    let best = null;
    for (const rawCode of codes) {
      const code = normalize(rawCode);
      for (const campaign of campaigns.product_discounts || []) {
        const campaignCodes =
          campaign.disc_meth === 'code' ? [campaign.disc_cd] : campaign.disc_codes || [];
        if (!campaignCodes.map(normalize).includes(code)) continue;
        if (!data.settings?.disable_markets && !(campaign.markets || []).includes(data.market))
          continue;
        if (intersects(tags, campaign.disc_ex_tags)) continue;
        for (const group of campaign.dg || []) {
          if (
            group.code_application !== 'all' &&
            !(
              group.code_application === 'spec' &&
              (group.specific_codes || []).map(normalize).includes(code)
            )
          )
            continue;
          if (
            group.customer_application !== 'all' &&
            !(
              group.customer_application === 'tags' &&
              (!(group.cust_tags || []).length ||
                intersects(data.customer_tags, group.cust_tags)) &&
              !intersects(data.customer_tags, group.ex_cust_tags)
            )
          )
            continue;
          const matches =
            group.base === 'prods'
              ? includesId(group.product_items, product.id)
              : group.base === 'vars'
                ? (group.product_variant_items || []).some((item) =>
                    includesId(item.vids, variant.id)
                  )
                : group.base === 'tags' && intersects(tags, group.tags);
          if (!matches || intersects(tags, group.ex_tags)) continue;
          const purchaseType = group.purchase_type || 'once';
          if (purchaseType !== 'both' && purchaseType !== (subscription ? 'subscription' : 'once'))
            continue;
          if ((group.sp === 'exclude' && sale) || (group.sp === 'only' && !sale)) continue;
          if (
            group.uvt &&
            (intersects(variantTags, group.xvt) ||
              ((group.vt || []).length && !intersects(variantTags, group.vt)))
          )
            continue;
          let amount = Number(group.disc_amt?.[data.currency] || 0);
          if (data.settings?.disable_multi_currency) {
            const values = Object.values(group.disc_amt || {});
            amount = values.length === 1 ? Math.round(Number(values[0]) * rate * 100) / 100 : 0;
          }
          const percentage = Number(group.disc_perc);
          const price = Number(variant.price);
          const finalPrice =
            group.calc === 'perc'
              ? Math.round(price * (1 - percentage / 100))
              : group.calc === 'amt'
                ? Math.max(0, Math.round(price - amount * 100))
                : price;
          if (!Number.isFinite(finalPrice) || finalPrice >= price || finalPrice < 0) continue;
          if (!best || finalPrice < best.finalPrice) {
            best = {
              code: rawCode.trim().toUpperCase(),
              finalPrice,
              percentage: group.calc === 'perc' ? percentage : null,
              saving: price - finalPrice
            };
          }
        }
      }
    }
    return best;
  }

  if (customElements.get('product-auto-discount-activator')) return;
  class ProductAutoDiscountActivator extends HTMLElement {
    static findOffer = findOffer;

    connectedCallback() {
      this.onVariant = (event) => {
        if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
        this.variantId = event.detail.variant?.id;
        this.refresh();
      };
      this.onChange = (event) => {
        if (
          event.target.name === 'selling_plan' ||
          event.target.dataset.radioType === 'selling_plan'
        )
          this.refresh();
      };
      this.onReady = () => this.refresh();
      document.addEventListener('product:variant-rendered', this.onVariant);
      document.addEventListener('DOMContentLoaded', this.onReady);
      this.closest('.product-information')?.addEventListener('change', this.onChange);
      window.addEventListener('load', this.onReady);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-rendered', this.onVariant);
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.closest('.product-information')?.removeEventListener('change', this.onChange);
      window.removeEventListener('load', this.onReady);
    }

    refresh() {
      try {
        const source = document.getElementById('co2_data');
        if (!source) return;
        const data = JSON.parse(source.textContent);
        const campaigns = JSON.parse(
          new TextDecoder().decode(
            Uint8Array.from(atob(data.campaigns), (char) => char.charCodeAt(0))
          )
        );
        const product = JSON.parse(this.querySelector('[data-auto-discount-product]').textContent);
        const variant = product.variants.find(
          (item) => String(item.id) === String(this.variantId || this.dataset.variantId)
        );
        const area = this.closest('.product-information');
        const plan = area?.querySelector(
          'input[name="selling_plan"]:checked, select[name="selling_plan"], input[name="selling_plan"][type="hidden"]'
        );
        const subscription = Boolean(plan?.value && plan.value !== '0');
        const offer = findOffer(
          data,
          campaigns,
          product,
          variant,
          this.dataset.codes.split(',').filter((code) => code.trim()),
          subscription,
          Number(window.Shopify?.currency?.rate || 1)
        );
        const output = this.querySelector('[data-auto-discount-output]');
        if (!offer) {
          output.replaceChildren();
          this.hidden = true;
          return;
        }
        const text =
          offer.percentage !== null
            ? `-${new Intl.NumberFormat(document.documentElement.lang || 'de').format(offer.percentage)}%`
            : `-${new Intl.NumberFormat(document.documentElement.lang || 'de', { style: 'currency', currency: data.currency }).format(offer.saving / 100)}`;
        const current = output.querySelector('[data-iwto-discount-code]');
        if (current?.dataset.iwtoDiscountCode === offer.code) {
          output.querySelector('[data-auto-discount-amount]').textContent = text;
        } else {
          const content = this.querySelector('template').content.cloneNode(true);
          content.querySelector('[data-auto-discount-code]').textContent = offer.code;
          content.querySelector('[data-auto-discount-amount]').textContent = text;
          content.querySelector('button').dataset.iwtoDiscountCode = offer.code;
          output.replaceChildren(content);
        }
        this.hidden = false;
      } catch (error) {
        this.querySelector('[data-auto-discount-output]')?.replaceChildren();
        this.hidden = true;
        console.warn('Automatic product discount could not be read.', error);
      }
    }
  }
  customElements.define('product-auto-discount-activator', ProductAutoDiscountActivator);
})();
